import { useEffect, useState, useRef } from "react";
import api from "../api/client";
import EditableSelect from "../components/EditableSelect";
import { useExpenseOptions } from "../context/ExpenseOptionsContext";

const CSS = `
.ex-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; flex-wrap:wrap; gap:12px; }
.ex-title  { font-size:18px; font-weight:700; color:#0d1a36; }

.ex-receipt { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; margin-bottom:12px; overflow:hidden; }
.ex-receipt-head { display:flex; align-items:center; gap:12px; padding:14px 16px; cursor:pointer;
  transition:background .15s; user-select:none; }
.ex-receipt-head:hover { background:#f4f8ff; }
.ex-chevron { font-size:10px; color:#6080aa; transition:transform .2s; flex-shrink:0; }
.ex-chevron.open { transform:rotate(90deg); }
.ex-shop-input { font-size:14px; font-weight:600; color:#0d1a36; flex:1; background:none;
  border:1px solid transparent; border-radius:5px; padding:3px 6px; font-family:inherit; min-width:120px; }
.ex-shop-input:hover, .ex-shop-input:focus { border-color:#c0d0eb; background:#f4f8ff; outline:none; }
.ex-date   { font-size:12px; color:#6080aa; }
.ex-date-input { font-size:12px; color:#6080aa; background:none; border:1px solid transparent; border-radius:5px;
  padding:2px 4px; font-family:inherit; cursor:pointer; }
.ex-date-input:hover, .ex-date-input:focus { border-color:#c0d0eb; background:#f4f8ff; outline:none; }
.ex-jump-btn { padding:7px 12px; border-radius:7px; border:1px solid #c0d0eb; background:#ffffff;
  cursor:pointer; font-size:14px; line-height:1; font-family:inherit; }
.ex-jump-btn:hover { border-color:#6080aa; background:#f4f8ff; }
.ex-jump-hidden { position:absolute; width:0; height:0; opacity:0; pointer-events:none; border:none; }
.ex-amount { font-size:14px; font-weight:700; color:#2563eb; font-variant-numeric:tabular-nums; }
.ex-del-btn { background:none; border:none; color:#dc2626; cursor:pointer; font-size:16px; opacity:.5; padding:0 0 0 10px; }
.ex-del-btn:hover { opacity:1; }

.ex-items { padding:0 16px 14px; border-top:1px solid #d5e3f5; }
.ex-items-table { width:100%; border-collapse:collapse; margin-top:10px; }
.ex-items-table th { font-size:10px; font-weight:600; letter-spacing:.06em; text-transform:uppercase;
  color:#6080aa; padding:0 8px 8px; text-align:left; }
.ex-items-table td { padding:7px 8px; border-bottom:1px solid #d5e3f5; vertical-align:middle; }
.ex-items-table tr:last-child td { border-bottom:none; }
.ex-td-name { font-size:13px; color:#0d1a36; }
.ex-td-qty  { font-size:12px; color:#6080aa; width:60px; }
.ex-td-price{ font-size:13px; font-weight:600; color:#2563eb; font-variant-numeric:tabular-nums; width:100px; }
.ex-td-cat  { width:140px; }
.ex-inline-select { background:#f4f8ff; border:1px solid #d5e3f5; border-radius:6px; padding:5px 8px;
  font-size:12px; color:#2a4a78; outline:none; width:100%; font-family:inherit; cursor:pointer; }
.ex-inline-select:focus { border-color:#059669; }

.ex-empty { font-size:13px; color:#6080aa; padding:20px 0; }
.ex-spinner-wrap { display:flex; justify-content:center; align-items:center; height:200px; }
.ex-spinner { width:32px; height:32px; border:3px solid #c0d0eb; border-top-color:#059669; border-radius:50%; animation:exspin .6s linear infinite; }
@keyframes exspin { to{ transform:rotate(360deg); } }

.ex-months { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:20px; }
.ex-month-btn { padding:7px 12px; border-radius:7px; border:1px solid #c0d0eb; background:#ffffff;
  color:#2a4a78; font-size:12px; font-weight:500; cursor:pointer; font-family:inherit; transition:all .15s; }
.ex-month-btn.active { border-color:#2563eb; color:#2563eb; font-weight:700; }
.ex-month-btn:hover:not(.active) { border-color:#6080aa; color:#0d1a36; }
`;

const MONTHS = Array.from({ length: 6 }, (_, i) => {
  const d = new Date(); d.setMonth(d.getMonth() - i);
  return d.toISOString().slice(0, 7);
});

function formatMonth(m) {
  const [year, mon] = m.split("-").map(Number);
  return new Date(Date.UTC(year, mon - 1, 1))
    .toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

function ReceiptCard({ receipt, month, onDelete, onUpdateReceipt, onDateMovedOut }) {
  const { categories, addOption } = useExpenseOptions();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(receipt.items || []);

  const patchCategory = async (itemId, category) => {
    await api.patch(`/expense/items/${itemId}`, { category });
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, category } : it));
  };

  const patchName = async (itemId, item_name) => {
    await api.patch(`/expense/items/${itemId}`, { item_name });
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, item_name } : it));
  };

  const patchPrice = async (itemId, price) => {
    const value = parseFloat(price);
    if (isNaN(value)) return;
    const res = await api.patch(`/expense/items/${itemId}`, { price: value });
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, price: value } : it));
    if (res.data.receipt_total_amount !== undefined) onUpdateReceipt(receipt.id, { total_amount: res.data.receipt_total_amount });
  };

  const patchQuantity = async (itemId, quantity) => {
    const value = parseFloat(quantity);
    if (isNaN(value)) return;
    const res = await api.patch(`/expense/items/${itemId}`, { quantity: value });
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, quantity: value } : it));
    if (res.data.receipt_total_amount !== undefined) onUpdateReceipt(receipt.id, { total_amount: res.data.receipt_total_amount });
  };

  const patchDate = async (e) => {
    const newDate = e.target.value;
    if (!newDate || newDate === String(receipt.receipt_date||"").slice(0,10)) return;
    await api.patch(`/expense/receipts/${receipt.id}`, { receipt_date: newDate });
    if (month !== "all" && newDate.slice(0,7) !== month) {
      onDateMovedOut(receipt.id);
    } else {
      onUpdateReceipt(receipt.id, { receipt_date: newDate });
    }
  };

  const patchShopName = async (e) => {
    const newName = e.target.value.trim();
    if (!newName || newName === receipt.shop_name) return;
    await api.patch(`/expense/receipts/${receipt.id}`, { shop_name: newName });
    onUpdateReceipt(receipt.id, { shop_name: newName });
  };

  return (
    <div className="ex-receipt">
      <div className="ex-receipt-head" onClick={() => setOpen(o => !o)}>
        <span className={`ex-chevron${open?" open":""}`}>▶</span>
        <input className="ex-shop-input" defaultValue={receipt.shop_name||""}
               onClick={e=>e.stopPropagation()} onBlur={patchShopName} />
        <input type="date" className="ex-date-input" defaultValue={String(receipt.receipt_date||"").slice(0,10)}
               onClick={e=>e.stopPropagation()} onBlur={patchDate} />
        <span className="ex-amount">{receipt.currency||"OMR"} {parseFloat(receipt.total_amount||0).toFixed(3)}</span>
        <button className="ex-del-btn" onClick={e=>{e.stopPropagation();onDelete(receipt.id);}}>×</button>
      </div>
      {open && (
        <div className="ex-items">
          <table className="ex-items-table">
            <thead>
              <tr><th>Item</th><th>Qty</th><th>Price</th><th>Category</th></tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id}>
                  <td className="ex-td-name">
                    <input className="ex-inline-select" defaultValue={it.item_name || it.name || ""}
                           onBlur={e => { if (e.target.value !== (it.item_name||it.name)) patchName(it.id, e.target.value); }} />
                  </td>
                  <td className="ex-td-qty">
                    <input className="ex-inline-select" style={{width:52}} type="number" min="0" step="1" defaultValue={Number(it.quantity ?? 1)}
                           onBlur={e => { if (parseFloat(e.target.value) !== parseFloat(it.quantity)) patchQuantity(it.id, e.target.value); }} />
                  </td>
                  <td className="ex-td-price">
                    {receipt.currency||"OMR"}{" "}
                    <input className="ex-inline-select" style={{display:"inline-block",width:90}} type="number" min="0" step="0.001" defaultValue={it.price ?? 0}
                           onBlur={e => { if (parseFloat(e.target.value) !== parseFloat(it.price)) patchPrice(it.id, e.target.value); }} />
                  </td>
                  <td className="ex-td-cat">
                    <EditableSelect className="ex-inline-select" value={it.category||"Others"} options={categories}
                                    onChange={v=>patchCategory(it.id, v)}
                                    onAddNew={v=>addOption("category", v)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Expenses() {
  const [month,    setMonth]    = useState(new Date().toISOString().slice(0, 7));
  const [receipts, setReceipts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const jumpRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get(month === "all" ? "/expense/receipts" : `/expense/receipts?month=${month}`)
      .then(r => setReceipts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month]);

  const deleteReceipt = async (id) => {
    await api.delete(`/expense/receipts/${id}`);
    setReceipts(prev => prev.filter(r => r.id !== id));
  };

  const updateReceipt = (id, patch) => {
    setReceipts(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  const removeReceiptFromView = (id) => {
    setReceipts(prev => prev.filter(r => r.id !== id));
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="ex-header">
        <div className="ex-title">🧾 Expenses</div>
      </div>

      <div className="ex-months">
        <button className={`ex-month-btn${month==="all"?" active":""}`} onClick={()=>setMonth("all")}>All</button>
        {MONTHS.map(m => (
          <button key={m} className={`ex-month-btn${m===month?" active":""}`} onClick={()=>setMonth(m)}>
            {formatMonth(m)}
          </button>
        ))}
        <button type="button" className="ex-jump-btn" title="Jump to a specific month"
                onClick={()=> jumpRef.current?.showPicker ? jumpRef.current.showPicker() : jumpRef.current?.click()}>
          📅
        </button>
        <input ref={jumpRef} type="month" className="ex-jump-hidden" value=""
               onChange={e=>e.target.value && setMonth(e.target.value)} />
      </div>

      {loading
        ? <div className="ex-spinner-wrap"><div className="ex-spinner"/></div>
        : receipts.length === 0
          ? <div className="ex-empty">No receipts found{month!=="all" && ` for ${formatMonth(month)}`}.</div>
          : receipts.map(r => (
              <ReceiptCard key={r.id} receipt={r} month={month} onDelete={deleteReceipt}
                           onUpdateReceipt={updateReceipt} onDateMovedOut={removeReceiptFromView} />
            ))
      }
    </>
  );
}
